import React, { useCallback, useEffect, useRef, useState } from 'react'
import getTimecode from '@Utils/GetTimecode'
import { safeMediaPlay } from '@Utils/SafeMediaPlay'
import PlayAudio from '@Images/play_audio.svg'
import PauseAudio from '@Images/pause_audio.svg'
import Close from '@Images/close_new.svg'

import './DesignVoiceSampleResultCard.scss'

interface DesignVoiceSampleResultCardProps {
  readonly url: string
  readonly label: string
  onRemove(): void
  onRegenerate(): void
  readonly regenerateDisabled: boolean
}

const DesignVoiceSampleResultCard: React.FC<DesignVoiceSampleResultCardProps> = props => {
  const { url, label, onRemove, onRegenerate, regenerateDisabled } = props

  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [playing, setPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)

  useEffect(() => {
    const audio = new Audio(url)

    const onPlay = () => setPlaying(true)
    const onPause = () => setPlaying(false)
    const onEnded = () => setPlaying(false)
    const onTimeUpdate = () => setCurrentTime(audio.currentTime)
    const onLoadedMetadata = () => setDuration(audio.duration || 0)
    const onDurationChange = () => setDuration(audio.duration || 0)

    audio.addEventListener('play', onPlay)
    audio.addEventListener('pause', onPause)
    audio.addEventListener('ended', onEnded)
    audio.addEventListener('timeupdate', onTimeUpdate)
    audio.addEventListener('loadedmetadata', onLoadedMetadata)
    audio.addEventListener('durationchange', onDurationChange)

    audio.src = url
    audio.load()
    audioRef.current = audio

    return () => {
      audio.pause()
      audio.removeEventListener('play', onPlay)
      audio.removeEventListener('pause', onPause)
      audio.removeEventListener('ended', onEnded)
      audio.removeEventListener('timeupdate', onTimeUpdate)
      audio.removeEventListener('loadedmetadata', onLoadedMetadata)
      audio.removeEventListener('durationchange', onDurationChange)
      audio.src = ''
      audio.load()
      audioRef.current = null
    }
  }, [url])

  const togglePlay = useCallback(() => {
    const audio = audioRef.current
    if (!audio) { return }

    if (playing) {
      audio.pause()
    } else {
      void safeMediaPlay(audio, { context: 'DesignVoiceSampleResultCard' })
    }
  }, [playing])

  const handleProgressClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current
    if (!audio || !duration) { return }

    const rect = event.currentTarget.getBoundingClientRect()
    const ratio = (event.clientX - rect.left) / rect.width
    audio.currentTime = Math.max(0, Math.min(duration, ratio * duration))
  }, [duration])

  const handleRegenerateClick = useCallback(() => {
    onRegenerate()
  }, [onRegenerate])

  const durationLabel = duration > 0 && Number.isFinite(duration)
    ? getTimecode(duration, false)
    : '0:00'

  const progressRatio = duration > 0 ? currentTime / duration : 0

  return (
    <div className="DesignVoiceSampleResultCard">
      <button
        type="button"
        className="DesignVoiceSampleResultCard__remove"
        onClick={onRemove}
        aria-label="Remove sample"
      >
        <Close />
      </button>
      <div className="DesignVoiceSampleResultCard__main">
        <button
          type="button"
          className="DesignVoiceSampleResultCard__play"
          onClick={togglePlay}
          aria-label={playing ? 'Pause' : 'Play'}
        >
          {playing ? <PauseAudio /> : <PlayAudio />}
        </button>
        <div className="DesignVoiceSampleResultCard__body">
          <div className="DesignVoiceSampleResultCard__title-row">
            <span className="DesignVoiceSampleResultCard__label">{label}</span>
            <span className="DesignVoiceSampleResultCard__duration">{durationLabel}</span>
          </div>
          <div
            className="DesignVoiceSampleResultCard__progress-track"
            onClick={handleProgressClick}
          >
            <div
              className="DesignVoiceSampleResultCard__progress-fill"
              style={{ width: `${progressRatio * 100}%` }}
            />
          </div>
        </div>
        <button
          type="button"
          className="DesignVoiceSampleResultCard__regenerate"
          onClick={handleRegenerateClick}
          disabled={regenerateDisabled}
          aria-label="Regenerate sample"
        >
          <span className="DesignVoiceSampleResultCard__regenerate-icon" aria-hidden>
            ↻
          </span>
        </button>
      </div>
    </div>
  )
}

export default DesignVoiceSampleResultCard
